import { UnknownAction } from '@reduxjs/toolkit'
import { BillingFieldKeys, DeliveryAddressFieldKeys, PersonalDataFieldKeys } from 'store/customer/enums'
import { updateAllInputs, updateInput } from 'store/customer/slice'
import { UpdateAllInputsPayload } from 'store/customer/types'
import { Address } from 'types/customer'
import { maskEmail, maskPhone, maskValue } from 'utils/format'
import type { RootState } from 'store/index'

/**
 * Masks PII fields in the Redux state before sending it to Sentry.
 * @param {RootState} state
 * @returns {RootState}
 */
export const stateTransformer = (state: RootState): RootState => {
  return {
    ...state,
    customer: {
      ...state.customer,
      inputs: {
        ...state.customer.inputs,
        deliveryAddress: {
          ...state.customer.inputs.deliveryAddress,
          address: {
            ...state.customer.inputs.deliveryAddress.address,
            streetName: maskValue(state.customer.inputs.deliveryAddress.address.streetName)
          }
        },
        personalData: {
          ...state.customer.inputs.personalData,
          firstName: maskValue(state.customer.inputs.personalData.firstName),
          lastName: maskValue(state.customer.inputs.personalData.lastName),
          email: maskEmail(state.customer.inputs.personalData.email),
          repeatEmail: maskEmail(state.customer.inputs.personalData.repeatEmail),
          mobilePhone: maskPhone(state.customer.inputs.personalData.mobilePhone)
        },
        billing: {
          ...state.customer.inputs.billing,
          correspondenceAddress: {
            ...state.customer.inputs.billing.correspondenceAddress,
            streetName: maskValue(state.customer.inputs.billing.correspondenceAddress.streetName)
          }
        }
      }
    }
  } satisfies RootState
}

/**
 * Masks PII fields in Redux actions before sending them to Sentry.
 * Handles customer/updateInput and customer/updateAllInputs action types.
 * @param {UnknownAction} action
 * @returns {UnknownAction}
 */
export const actionTransformer = (action: UnknownAction): UnknownAction => {
  const isUpdateInput = updateInput.match(action)
  const isUpdateAllInputs = updateAllInputs.match(action)

  if (!isUpdateInput && !isUpdateAllInputs) {
    return action
  }

  if (isUpdateInput) {
    const { payload } = action

    // Mask emails
    if (payload.key === PersonalDataFieldKeys.EMAIL || payload.key === PersonalDataFieldKeys.REPEAT_EMAIL) {
      return {
        ...action,
        payload: {
          ...action.payload,
          value: maskEmail(payload.value)
        }
      }
    }

    // Mask first/last name
    if (payload.key === PersonalDataFieldKeys.FIRSTNAME || payload.key === PersonalDataFieldKeys.LASTNAME) {
      return {
        ...action,
        payload: {
          ...action.payload,
          value: maskValue(payload.value)
        }
      }
    }

    // Mask phone number
    if (payload.key === PersonalDataFieldKeys.MOBILE_PHONE) {
      return {
        ...action,
        payload: {
          ...action.payload,
          value: maskPhone(payload.value)
        }
      }
    }

    // Mask street name
    if (payload.key === DeliveryAddressFieldKeys.ADDRESS || payload.key === BillingFieldKeys.CORRESPONDENCE_ADDRESS) {
      return {
        ...action,
        payload: {
          ...action.payload,
          value: {
            ...payload.value,
            streetName: maskValue(payload.value.streetName)
          } satisfies Address
        }
      }
    }
  }

  if (isUpdateAllInputs) {
    const { payload } = action

    return {
      ...action,
      payload: {
        ...payload,
        inputs: {
          ...payload.inputs,
          deliveryAddress: {
            ...payload.inputs.deliveryAddress,
            address: payload.inputs.deliveryAddress?.address
              ? {
                  ...payload.inputs.deliveryAddress.address,
                  streetName: maskValue(payload.inputs.deliveryAddress.address.streetName)
                }
              : payload.inputs.deliveryAddress?.address
          },
          billing: {
            ...payload.inputs.billing,
            correspondenceAddress: payload.inputs.billing?.correspondenceAddress
              ? {
                  ...payload.inputs.billing.correspondenceAddress,
                  streetName: maskValue(payload.inputs.billing.correspondenceAddress.streetName)
                }
              : payload.inputs.billing?.correspondenceAddress
          },
          personalData: {
            ...payload.inputs.personalData,
            firstName: maskValue(payload.inputs.personalData.firstName),
            lastName: maskValue(payload.inputs.personalData.lastName),
            email: maskEmail(payload.inputs.personalData.email),
            repeatEmail: maskEmail(payload.inputs.personalData.repeatEmail),
            mobilePhone: maskPhone(payload.inputs.personalData.mobilePhone)
          }
        }
      } satisfies UpdateAllInputsPayload
    }
  }

  return action
}
